import {
  DEFAULT_LAYOUT_SETTINGS,
  Filler,
  PublisherPaginationSettings,
  HeaderSettingsConfig
} from '../../types/publisherPaginationSettings';
import { ESnapshotExists, ETemplate } from '../../types';
import { wrapError, wrapSuccess } from '../../types/responses';
import {
  OptimizerHyperparams,
  AutoPaginationRequestLayout,
  AutoLayoutRequestElement,
  LayoutBlockFixedElement
} from '../../types/api';

/**
 * Calculate max whitespace based on layout size using percentage of layout area
 * Uses linear scaling based on the configured percentage
 */
function calculateMaxWhitespaceForLayout(
  layoutWidth: number,
  layoutHeight: number,
  maxWhitespacePercentage: number
): number {
  const layoutArea = layoutWidth * layoutHeight;
  // Calculate max whitespace as percentage of total layout area
  return Math.floor(layoutArea * (maxWhitespacePercentage / 100));
}

const getTotalColumnInches = (orderBlocks: AutoLayoutRequestElement[]) => {
  return orderBlocks.reduce((acc, notice) => {
    const biggestVariant = notice.variants.reduce((biggest, variant) => {
      return Math.max(biggest, variant.height * variant.width);
    }, 0);
    return acc + biggestVariant;
  }, 0);
};

export const layoutIsValidForOrders = (
  layout: AutoPaginationRequestLayout,
  template: ESnapshotExists<ETemplate>,
  orderBlocks: AutoLayoutRequestElement[],
  hyperparams: OptimizerHyperparams
) => {
  const totalFixedHeightArea = layout.fixed_elements.reduce((acc, element) => {
    return acc + element.height;
  }, 0);
  const totalOrderArea = getTotalColumnInches(orderBlocks);

  const potentialWhitespace =
    layout.width * layout.height - (totalOrderArea + totalFixedHeightArea);

  // if the whitespace required is too much, return false
  const maxWhitespaceForLayout = calculateMaxWhitespaceForLayout(
    layout.width,
    layout.height,
    hyperparams.max_whitespace_percentage
  );
  if (potentialWhitespace > maxWhitespaceForLayout) {
    return false;
  }

  const widthIsFeasible = orderBlocks.some(block =>
    block.variants.some(variant => variant.width <= layout.width)
  );
  if (!widthIsFeasible) {
    return false;
  }

  const templatePageHeight = template.data().styles?.pageHeight ?? 0;
  const isTooTall = layout.height > templatePageHeight;
  if (isTooTall) {
    return false;
  }

  const layoutColumns = template.data().styles?.columnCount ?? 0;
  const isTooWide = layout.width > layoutColumns;
  if (isTooWide) {
    return false;
  }

  return true;
};

const fillerIsValidForLayout = (
  filler: Filler,
  layout: { columns: number }
) => {
  if (filler.columns === 3 && layout.columns === 4) {
    return false;
  }
  return true;
};

export const getPotentialPublisherLayouts = (
  publisherPaginationSettings: ESnapshotExists<PublisherPaginationSettings>,
  template: ESnapshotExists<ETemplate>
) => {
  const { layoutSettings } = publisherPaginationSettings.data();
  const { minHeight, heightIncrements } =
    layoutSettings || DEFAULT_LAYOUT_SETTINGS;
  const layouts: AutoPaginationRequestLayout[] = [];

  const templateHeight = template.data().styles?.pageHeight;
  if (!templateHeight) {
    return wrapError(new Error('Template height not found'));
  }
  const columns = template.data().styles?.columnCount ?? 1;

  for (
    let layoutHeight = minHeight;
    layoutHeight < templateHeight;
    layoutHeight += heightIncrements
  ) {
    for (let layoutWidth = 1; layoutWidth <= columns; layoutWidth++) {
      layouts.push({
        id: `auto-paginate-${layoutHeight}-${layoutWidth}-height`,
        width: layoutWidth,
        height: layoutHeight,
        fixed_elements: [],
        max_whitespace: 0,
        optional_elements: []
      });
    }
  }
  return wrapSuccess(layouts);
};

const getFixedElementsForColunmnWidth = (
  headerSettings: HeaderSettingsConfig | null | undefined,
  { columns }: { columns: number }
) => {
  if (!headerSettings) {
    return [];
  }
  const fixedElements: LayoutBlockFixedElement[] = [];

  const relevantHeader = headerSettings.headers.find(header =>
    header.applyToColumns.includes(columns)
  );
  if (relevantHeader) {
    for (let i = 0; i < columns; i += relevantHeader.width) {
      fixedElements.push({
        x: i,
        y: 0,
        width: relevantHeader.width,
        height: relevantHeader.height,
        id: relevantHeader.id
      });
    }
  }
  return fixedElements;
};

export const getLayoutsForAutoPaginationRequest = async (
  publisherPaginationSettings: ESnapshotExists<PublisherPaginationSettings>,
  hyperparams: OptimizerHyperparams,
  template: ESnapshotExists<ETemplate>,
  orderBlocks: AutoLayoutRequestElement[]
) => {
  if (!orderBlocks.length) {
    return wrapError(
      new Error('No order blocks provided, so no layouts can be generated')
    );
  }
  const maxFillersForPublisherLayouts = 1;

  const { fillers, headerSettings } = publisherPaginationSettings.data();

  const [potentialLayoutsError, potentialLayouts] =
    await getPotentialPublisherLayouts(publisherPaginationSettings, template);
  if (potentialLayoutsError) {
    return wrapError(potentialLayoutsError);
  }

  const layouts: AutoPaginationRequestLayout[] = potentialLayouts.map(
    allowedLayout => {
      const fixedElements = getFixedElementsForColunmnWidth(headerSettings, {
        columns: allowedLayout.width
      });

      const layout = {
        id: allowedLayout.id,
        width: allowedLayout.width,
        height: allowedLayout.height,
        fixed_elements: fixedElements,
        max_whitespace: calculateMaxWhitespaceForLayout(
          allowedLayout.width,
          allowedLayout.height,
          hyperparams.max_whitespace_percentage
        ),
        optional_elements: fillers
          .filter(filler =>
            fillerIsValidForLayout(filler, { columns: allowedLayout.width })
          )
          .flatMap(filler =>
            Array.from({ length: maxFillersForPublisherLayouts }, (_, i) => ({
              ...filler,
              width: filler.columns,
              id: `${filler.name}-${allowedLayout.id}-${filler.id}-${i}`
            }))
          )
      };
      return layout;
    }
  );

  // dynamically compute the max number of full page column layouts to consider
  const fullPageLayoutColumnInches =
    Math.floor(template.data().styles?.pageHeight ?? 0) *
    (template.data().styles?.columnCount ?? 1);
  const totalColumnInches = getTotalColumnInches(orderBlocks);
  const fullPageLayoutsToInclude = Math.ceil(
    totalColumnInches / fullPageLayoutColumnInches
  );

  // if we have just one order, create a custom-sized template for it
  if (orderBlocks.length === 1) {
    const [onlyBlock] = orderBlocks;
    const defaultVariant = onlyBlock.variants[0];
    layouts.push({
      id: `custom-sized-order-${onlyBlock.id}`,
      width: defaultVariant.width,
      height: defaultVariant.height,
      max_whitespace: 1,
      fixed_elements: [],
      optional_elements: []
    });
  }

  for (let i = 0; i < fullPageLayoutsToInclude; i++) {
    const fixedElements = getFixedElementsForColunmnWidth(headerSettings, {
      columns: template.data().styles?.columnCount ?? 1
    });

    layouts.push({
      id: `auto-paginate-${i}-full-page`,
      width: template.data().styles?.columnCount ?? 1,
      height: Math.floor(template.data().styles?.pageHeight ?? 0),
      max_whitespace: calculateMaxWhitespaceForLayout(
        template.data().styles?.columnCount ?? 1,
        Math.floor(template.data().styles?.pageHeight ?? 0),
        hyperparams.max_whitespace_percentage
      ),
      fixed_elements: fixedElements,
      optional_elements: fillers
        .filter(filler =>
          fillerIsValidForLayout(filler, {
            columns: template.data().styles?.columnCount ?? 1
          })
        )
        .flatMap(filler =>
          Array.from({ length: filler.maxOccurrences }, (_, j) => ({
            ...filler,
            width: filler.columns,
            id: `${filler.name}-big-${i}-${filler.id}-${j}`
          }))
        )
    });
  }
  for (let col = 1; col < (template?.data()?.styles?.columnCount || 1); col++) {
    const fixedElements = getFixedElementsForColunmnWidth(headerSettings, {
      columns: col
    });

    layouts.push({
      id: `auto-paginate-${col}-col`,
      width: col,
      height: Math.floor(template?.data()?.styles?.pageHeight ?? 0),
      max_whitespace: calculateMaxWhitespaceForLayout(
        col,
        Math.floor(template?.data()?.styles?.pageHeight ?? 0),
        hyperparams.max_whitespace_percentage
      ),
      fixed_elements: fixedElements,
      optional_elements: fillers
        .filter(filler => fillerIsValidForLayout(filler, { columns: col }))
        .flatMap(filler =>
          Array.from({ length: filler.maxOccurrences }, (_, j) => ({
            ...filler,
            width: filler.columns,
            id: `${filler.name}-small-${col}-${filler.id}-${j}`
          }))
        )
    });
  }

  const possibleLayouts = layouts.filter(layout => {
    return layoutIsValidForOrders(layout, template, orderBlocks, hyperparams);
  });

  if (!possibleLayouts.length) {
    return wrapError(
      new Error(
        'No layouts found that could work with the request. This probably means that you need a new layout size for small publication issues to be able to auto-paginate.'
      )
    );
  }

  return wrapSuccess(possibleLayouts);
};
